import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Form, Typography } from "antd";
import tokens from "../../theme/tokens";
import { buildCategoryOptions } from "./NegativeExampleModal";

const { TextArea } = Input;
const { Text } = Typography;

const REASON_MIN_CHARS = 20;

/**
 * Admin-side edit modal for a NegativeExample row. Lets admins change the
 * "actually it should be" category, refine the reason, or capture
 * internal-only notes. The "negative for" category is NOT editable here —
 * it represents the AI's wrong prediction, which is a fixed fact about
 * the original mistake.
 */
const EditNegativeExampleModal = ({
  open,
  row,
  categories = [],
  saving = false,
  error = "",
  onCancel,
  onSubmit,
}) => {
  const options = useMemo(() => buildCategoryOptions(categories), [categories]);

  const negativeFor =
    row?.negative_for_category || row?._category || row?.doc_category || "";

  const [actualCategory, setActualCategory] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState({ actual: false, reason: false });

  useEffect(() => {
    if (!open) return;
    setActualCategory(row?.actual_category || "");
    setReason(row?.reason || "");
    setNotes(row?.notes || "");
    setTouched({ actual: false, reason: false });
  }, [open, row]);

  const reasonTrimmedLen = reason.trim().length;
  const reasonValid = reasonTrimmedLen >= REASON_MIN_CHARS;
  const actualValid =
    Boolean(actualCategory) && actualCategory !== negativeFor;

  const canSubmit = actualValid && reasonValid && !saving;

  const handleSubmit = () => {
    if (!canSubmit) {
      setTouched({ actual: true, reason: true });
      return;
    }
    onSubmit({
      actual_category: actualCategory,
      reason: reason.trim(),
      notes: notes.trim(),
    });
  };

  const filename = row?.filename || row?.document?.name || row?.document_name || "—";

  return (
    <Modal
      open={open}
      title="Edit report"
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Save"}
      cancelText="Cancel"
      okButtonProps={{
        disabled: !canSubmit,
        "data-testid": "edit-negative-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="edit-negative-modal"
    >
      <Form layout="vertical">
        <Form.Item label="Document">
          <div
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              background: tokens.surface.page,
              border: `1px solid ${tokens.border.light}`,
              color: tokens.text.primary,
              fontFamily: tokens.typography.fontFamily,
              fontSize: 13,
              wordBreak: "break-word",
            }}
          >
            {filename}
          </div>
        </Form.Item>

        <Form.Item label="AI's wrong category">
          <div style={{ color: tokens.text.secondary, fontSize: 13 }}>
            {negativeFor || "—"}
          </div>
        </Form.Item>

        <Form.Item
          label="Correct category"
          required
          validateStatus={touched.actual && !actualValid ? "error" : ""}
          help={
            touched.actual && !actualValid
              ? actualCategory === negativeFor
                ? "Pick a category that's different from the AI's wrong category."
                : "Pick the correct category."
              : null
          }
        >
          <Select
            value={actualCategory || undefined}
            onChange={(value) => {
              setActualCategory(value);
              setTouched((t) => ({ ...t, actual: true }));
            }}
            placeholder="Pick the right category"
            disabled={saving}
            showSearch
            optionFilterProp="label"
            options={options.map((opt) => ({
              ...opt,
              disabled: opt.value === negativeFor,
            }))}
            data-testid="edit-negative-actual-category"
          />
        </Form.Item>

        <Form.Item
          label="Why was the AI wrong?"
          required
          validateStatus={touched.reason && !reasonValid ? "error" : ""}
          help={
            touched.reason && !reasonValid
              ? `Give at least ${REASON_MIN_CHARS} characters of context. (${reasonTrimmedLen}/${REASON_MIN_CHARS})`
              : `${reasonTrimmedLen}/${REASON_MIN_CHARS} characters`
          }
        >
          <TextArea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, reason: true }))}
            disabled={saving}
            placeholder="A sentence or two of context."
            data-testid="edit-negative-reason"
          />
        </Form.Item>

        <Form.Item
          label="Internal notes (optional)"
          tooltip="Visible to admins only."
        >
          <TextArea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
            placeholder="Optional context for other admins."
            data-testid="edit-negative-notes"
          />
        </Form.Item>

        {error && (
          <Text type="danger" role="alert" data-testid="edit-negative-error">
            {error}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

EditNegativeExampleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  categories: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      results: PropTypes.array,
      data: PropTypes.array,
      categories: PropTypes.array,
    }),
  ]),
  saving: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default EditNegativeExampleModal;
